import { useTranslation } from "react-i18next";

// Real screens from the Photohawk Personal app (src/assets/home/app/<lang>/<screen>-{300,600}.webp, made by
// scripts/brand-assets/app-screens.sh from the app's raw 1320x2868 simulator captures: the real app over CC0 demo
// photos with invented names; credits in src/assets/home/CREDITS.md). Each site language shows its own locale's
// captures; any other language falls back to English. The site draws the phone frame (PhoneFrame).

const FILES = import.meta.glob<string>("../../assets/home/app/*/*.webp", { eager: true, import: "default" });

/** The screens, in the order the strip shows them. Only features the app has today (no groups, no trip mode). */
export const APP_SCREENS = ["people", "search", "person", "share", "detail"] as const;
export type AppScreen = (typeof APP_SCREENS)[number];

/** The site languages that have their own captures (pt is the app's pt-BR, zh its zh-Hans). */
export const SCREEN_LANGS = ["en", "de", "es", "it", "ja", "ms", "pt", "zh"] as const;

/** The raw captures' size: every screen keeps this aspect ratio. */
export const SCREEN_WIDTH = 1320;
export const SCREEN_HEIGHT = 2868;

export interface Shot {
  src: string;
  srcSet: string;
}

const file = (lang: string, screen: AppScreen, w: 300 | 600): string | undefined =>
  FILES[`../../assets/home/app/${lang}/${screen}-${w}.webp`];

/** A screen in a site language (its base tag: "pt-BR" → "pt"), or in English where that language has none. */
export const appShot = (screen: AppScreen, language?: string): Shot => {
  const base = (language || "en").toLowerCase().split("-")[0];
  const lang = (SCREEN_LANGS as readonly string[]).includes(base) && file(base, screen, 300) ? base : "en";
  const w300 = file(lang, screen, 300)!;
  const w600 = file(lang, screen, 600)!;
  return { src: w300, srcSet: `${w300} 300w, ${w600} 600w` };
};

/** The app screen for the page's current language. */
export const useAppShot = (screen: AppScreen): Shot => {
  const { i18n } = useTranslation();
  return appShot(screen, i18n.language);
};

/**
 * A phone around an app screen: a near-black bezel with the screen's rounded corners (the capture already has the
 * status bar and the Dynamic Island), a hairline edge and a soft, cool shadow that suits the site's light grounds.
 * Sized by its container's width; `sizes` says how wide it shows.
 */
export const PhoneFrame = ({
  shot,
  alt,
  sizes,
  eager = false,
  className = "",
}: {
  shot: Shot;
  alt: string;
  sizes: string;
  eager?: boolean;
  className?: string;
}) => (
  <div
    className={`rounded-[17%/7.8%] bg-[#0E1117] p-[3.4%] shadow-[0_1px_2px_rgba(18,23,33,0.12),0_18px_40px_rgba(18,23,33,0.18),inset_0_0_0_1.5px_#2B313C] ${className}`}
    data-testid="app-phone"
  >
    <img
      src={shot.src}
      srcSet={shot.srcSet}
      sizes={sizes}
      width={SCREEN_WIDTH}
      height={SCREEN_HEIGHT}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      className="block h-auto w-full rounded-[13.5%/6.2%] bg-white"
    />
  </div>
);
